import React, { FC, useEffect, useState } from 'react';
import { useMeasure, useToggle } from 'react-use';
import { twMerge } from 'tailwind-merge';

import NftCardBack from '../nftCardBack/NftCardBack';
import { NftCardContext } from '../nftCardContext/NftCardContext';
import NftCardFront, { INftCardFrontProps } from '../nftCardFront/NftCardFront';
import { INftCardAsset, NftCardSizeType } from '../types';
import styles from './NftCard.module.scss';

export interface INftCardProps extends INftCardFrontProps {
  size?: NftCardSizeType;
  asset: INftCardAsset | undefined;
  onForceToggle?: boolean;
  onCallback?: (slide: number) => void;
  loading?: boolean;
  disableHover?: boolean;
  frontBackgroundClassName?: string;
  backBackgroundClassName?: string;
  nameClassName?: string;
  cardMarkerClassName?: string;
  cardMarkerSizeClassName?: string;
  cardMarkerImageClassName?: string;
  categoryIdBadgeClassName?: string;
  nameContainerClassName?: string;
  nftCardPanelClassName?: string;
  style?: React.CSSProperties;
  imageTopRightContent?: React.ReactNode;
  imageBottomContent?: React.ReactNode;
  markerInBadge?: boolean;
}

const NftCard: FC<INftCardProps> = ({
  size = 'medium',
  asset,
  toggleable = true,
  onForceToggle,
  children,
  asLinkTo,
  isOverbid,
  isSold,
  onCallback,
  loading = false,
  disableHover = false,
  className,
  frontBackgroundClassName,
  backBackgroundClassName,
  nameClassName,
  nameClampedClassName,
  cardMarkerClassName,
  cardMarkerSizeClassName,
  cardMarkerImageClassName,
  addressClassName,
  sharesClassName,
  categoryIdBadgeClassName,
  nameContainerClassName,
  nftCardPanelClassName,
  style,
  imageTopRightContent,
  imageBottomContent,
  markerInBadge,
}) => {
  const [toggled, onToggle] = useToggle(false);
  const [animationClassName, setAnimationClassName] = useState<string | undefined>();
  const [ref, { height }] = useMeasure<HTMLDivElement>();

  const handleToggle = () => {
    toggled
      ? setAnimationClassName(styles['card--animating-reverse'])
      : setAnimationClassName(styles['card--animating']);

    onToggle();
  };

  useEffect(() => {
    toggled && onForceToggle && handleToggle();
  }, [toggled, onForceToggle]);

  const handleAnimationEnd = () => !loading && setAnimationClassName(undefined);

  // The back face hosts a Swiper (NftCardBackSlider). Swiper always registers
  // touch listeners on mount, and on iOS a backface-hidden element can still
  // receive touch hit-testing even while visually flipped away — so an
  // always-mounted back swallows the vertical pan gesture used to scroll the
  // page. Only mount the back once the flip is actually underway: `toggled`
  // flips to its new value synchronously when the flip starts (see
  // handleToggle above), so gating on `toggled` mounts the back right as the
  // open animation begins (plenty of time before it rotates into view) and
  // keeps it mounted through the close animation (animationClassName still
  // reads 'card--animating-reverse' for that 1s) so the reverse flip never
  // pops. Once the close animation ends, the back unmounts again, tearing
  // down the Swiper and its touch listeners while the card sits front-facing.
  const showBack = toggled || animationClassName === styles['card--animating-reverse'];

  return (
    <div
      ref={ref}
      data-testid="nft-card"
      className={twMerge(
        styles['card'],
        toggled && styles['card--toggled'],
        disableHover && styles['card--no-hover'],
        animationClassName,
        className
      )}
      style={style}
      onAnimationEnd={handleAnimationEnd}
    >
      <NftCardContext.Provider
        value={{ size, height, asset, toggled, onToggle: handleToggle, onCallback, loading, onForceToggle }}
      >
        <NftCardFront
          toggleable={toggleable}
          asLinkTo={asLinkTo}
          isOverbid={isOverbid}
          isSold={isSold}
          nameClassName={nameClassName}
          nameClampedClassName={nameClampedClassName}
          cardMarkerClassName={cardMarkerClassName}
          cardMarkerSizeClassName={cardMarkerSizeClassName}
          cardMarkerImageClassName={cardMarkerImageClassName}
          addressClassName={addressClassName}
          sharesClassName={sharesClassName}
          categoryIdBadgeClassName={categoryIdBadgeClassName}
          nameContainerClassName={nameContainerClassName}
          nftCardPanelClassName={nftCardPanelClassName}
          imageTopRightContent={imageTopRightContent}
          imageBottomContent={imageBottomContent}
          markerInBadge={markerInBadge}
        >
          {children}
        </NftCardFront>
        {showBack && <NftCardBack backBackgroundClassName={backBackgroundClassName} />}
      </NftCardContext.Provider>
      {/* front view background */}
      <div
        className={twMerge([
          'backface-hidden tw-absolute tw-inset-x-0 tw-inset-y-0 tw-z-[-1] tw-rounded-2xl',
          frontBackgroundClassName,
        ])}
      />
      {/* back view background */}
      <div
        className={twMerge([
          'backface-hidden tw-absolute tw-inset-x-0 tw-inset-y-0 tw-z-[-1] tw-rounded-2xl [transform:rotateY(180deg)]',
          backBackgroundClassName,
        ])}
      />
    </div>
  );
};

export default NftCard;
